import { 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  Paper, 
  Box, 
  IconButton, 
  Chip,
  Avatar,
  LinearProgress,
  styled,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Stack,
  Tooltip,
  Badge,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom,
  Alert,
  TableHead,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { 
  Delete, 
  Edit, 
  Inventory, 
  Analytics, 
  AttachMoney, 
  Add,
  Category,
  QrCode,
  CalendarToday,
  LocalShipping,
  Storage,
  Warning,
  CheckCircle,
  Timeline,
  Receipt,
  Description,
  Info,
  PriceChange,
  TrendingUp,
  TrendingDown,
  Balance,
  Factory,
  Assignment,
  Restaurant,
  Error,
  Close,
  DeleteForever,
  Warning as WarningIcon,
  ViewList,
  ViewModule,
  Search,
  FilterList,
  MoreVert,
  Print,
  Download,
  Share,
  Visibility,
  FilterAlt
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import Product from "../../Services/FoodCost";

const StyledCard = styled(Paper)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #eaeef2',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 12px 24px -8px rgba(0, 40, 80, 0.15)',
    transform: 'translateY(-4px)',
    borderColor: '#b8d1e0'
  }
}));

const MetricBox = styled(Box)(({ theme }) => ({
  background: '#f8fafc',
  borderRadius: '12px',
  padding: '12px',
  border: '1px solid #e2e8f0',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#f1f5f9',
    borderColor: '#94a3b8'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#475569',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem',
    color: '#2563eb'
  }
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: '1px dashed #e2e8f0',
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const StatusBadge = styled(Chip)(({ theme, status }) => {
  const colors = {
    inStock: { bg: '#e6f7e6', color: '#2e7d32', border: '#a5d6a7' },
    lowStock: { bg: '#fff3e0', color: '#ed6c02', border: '#ffb74d' },
    outOfStock: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' }
  };
  const selected = colors[status] || colors.inStock;
  
  return {
    backgroundColor: selected.bg,
    color: selected.color,
    borderColor: selected.border,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '24px',
    '& .MuiChip-label': {
      px: 1.5
    }
  };
});

// List View Component
const ProductListView = ({ products, handleEdit, handleDelete, onProductSelect, categories }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleCategoryClick = (event) => {
    setCategoryAnchorEl(event.currentTarget);
  };

  const handleCategoryClose = () => {
    setCategoryAnchorEl(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setPage(0);
    handleCategoryClose();
  };

  // Filter products by search term and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
      (product.category && product.category === selectedCategory) ||
      (selectedCategory === 'uncategorized' && !product.category);
    
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'margin') {
      aVal = (a.soldPrice - a.costPrice);
      bVal = (b.soldPrice - b.costPrice);
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get category color
  const getCategoryColor = (categoryName) => {
    const colors = {
      'Entrées': '#10b981',
      'Plats Principaux': '#3b82f6',
      'Desserts': '#f59e0b',
      'Boissons': '#8b5cf6',
      'Apéritifs': '#ef4444',
      'Accompagnements': '#14b8a6'
    };
    return colors[categoryName] || '#6b7280';
  };

  return (
    <Box>
      {/* Search and Filter Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Rechercher un produit..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        
        {/* Category Filter */}
        <Button
          variant={selectedCategory !== 'all' ? "contained" : "outlined"}
          startIcon={<Category />}
          onClick={handleCategoryClick}
          size="small"
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            ...(selectedCategory !== 'all' && {
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' }
            })
          }}
        >
          {selectedCategory === 'all' ? 'Toutes catégories' : 
           selectedCategory === 'uncategorized' ? 'Non catégorisés' : selectedCategory}
        </Button>
        
        <Menu
          anchorEl={categoryAnchorEl}
          open={Boolean(categoryAnchorEl)}
          onClose={handleCategoryClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              minWidth: 200,
              maxHeight: 400
            }
          }}
        >
          <MenuItem onClick={() => handleCategorySelect('all')}>
            <ListItemIcon>
              <FilterAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText>Toutes les catégories</ListItemText>
            {selectedCategory === 'all' && <CheckCircle fontSize="small" color="primary" />}
          </MenuItem>
          <MenuItem onClick={() => handleCategorySelect('uncategorized')}>
            <ListItemIcon>
              <Error fontSize="small" />
            </ListItemIcon>
            <ListItemText>Non catégorisés</ListItemText>
            {selectedCategory === 'uncategorized' && <CheckCircle fontSize="small" color="primary" />}
          </MenuItem>
          <Divider />
          {categories.map((category) => (
            <MenuItem 
              key={category} 
              onClick={() => handleCategorySelect(category)}
            >
              <ListItemIcon>
                <Category sx={{ color: getCategoryColor(category) }} fontSize="small" />
              </ListItemIcon>
              <ListItemText>{category}</ListItemText>
              {selectedCategory === category && <CheckCircle fontSize="small" color="primary" />}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Active Filters Display */}
      {(searchTerm || selectedCategory !== 'all') && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {searchTerm && (
            <Chip
              label={`Recherche: ${searchTerm}`}
              size="small"
              onDelete={() => setSearchTerm('')}
              sx={{ borderRadius: '12px' }}
            />
          )}
          {selectedCategory !== 'all' && (
            <Chip
              label={`Catégorie: ${selectedCategory === 'uncategorized' ? 'Non catégorisés' : selectedCategory}`}
              size="small"
              onDelete={() => setSelectedCategory('all')}
              sx={{ borderRadius: '12px', bgcolor: '#e0e7ff', color: '#3730a3' }}
            />
          )}
        </Box>
      )}

      {/* Results Count */}
      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
        {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
      </Typography>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('name')}
              >
                Produit {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('category')}
              >
                Catégorie {sortBy === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('sku')}
                align="right"
              >
                Référence {sortBy === 'sku' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('costPrice')}
                align="right"
              >
                Prix d'achat {sortBy === 'costPrice' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('soldPrice')}
                align="right"
              >
                Prix de vente {sortBy === 'soldPrice' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => handleSort('margin')}
                align="right"
              >
                Marge {sortBy === 'margin' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Inventory sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary">
                      Aucun produit trouvé
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Essayez de modifier vos critères de recherche
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProducts.map((product) => {
                const margin = (product.soldPrice || 0) - (product.costPrice || 0);
                const marginPercent = (margin / (product.soldPrice || 1)) * 100;
                
                return (
                  <TableRow 
                    key={product.id}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: alpha('#2563eb', 0.1) }}>
                          <Restaurant sx={{ fontSize: 20, color: '#2563eb' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {product.name}
                          </Typography>
                          {product.description && (
                            <Typography variant="caption" color="textSecondary">
                              {product.description.substring(0, 50)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.category ? (
                        <Chip 
                          label={product.category}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(getCategoryColor(product.category), 0.1),
                            color: getCategoryColor(product.category),
                            fontWeight: 500,
                            borderRadius: '12px'
                          }}
                        />
                      ) : (
                        <Chip 
                          label="Non catégorisé"
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: '12px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {product.sku || `REF-${product.id?.slice(-4)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="#0369a1" fontWeight={500}>
                        {(product.costPrice || 0).toFixed(3)} TND
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="#166534" fontWeight={500}>
                        {(product.soldPrice || 0).toFixed(3)} TND
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {margin.toFixed(3)} TND
                        </Typography>
                        <Typography variant="caption" color={marginPercent > 30 ? 'success.main' : marginPercent > 15 ? 'warning.main' : 'error.main'}>
                          {marginPercent.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Voir la fiche technique">
                        <IconButton 
                          size="small" 
                          onClick={() => onProductSelect && onProductSelect(product)}
                          sx={{ color: '#2563eb' }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(product)}
                          sx={{ color: '#475569' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(product.id, product.name)}
                          sx={{ color: '#ef4444' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {filteredProducts.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      )}
    </Box>
  );
};

// Card View Component (Fiche Technique)
const ProductCardView = ({ products, handleEdit, handleDelete, categories }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);
  
  return (
    <Box>
      {/* Category Filter for Card View */}
      {categories.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="Tous"
            onClick={() => setSelectedCategory('all')}
            color={selectedCategory === 'all' ? 'primary' : 'default'}
            sx={{ borderRadius: '20px' }}
          />
          {categories.map(cat => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setSelectedCategory(cat)}
              color={selectedCategory === cat ? 'primary' : 'default'}
              sx={{ borderRadius: '20px' }}
            />
          ))}
        </Box>
      )}
      
      <Grid container spacing={3}>
        {filteredProducts.map((product, idx) => (
          <Grid item xs={12} md={6} lg={4} key={product.id}>
            <TechnicalSheet 
              product={product}
              handleEdit={handleEdit}
              handleDelete={() => handleDelete(product.id, product.name)}
              index={idx}
            />
          </Grid>
        ))}
      </Grid>
      
      {filteredProducts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Inventory sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
          <Typography variant="body1" color="textSecondary">
            Aucun produit dans cette catégorie
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Technical Sheet Component
const TechnicalSheet = ({ product, handleEdit, handleDelete, index }) => {
  const margin = (product.soldPrice || 0) - (product.costPrice || 0);
  const marginPercent = (margin / (product.soldPrice || 1)) * 100;

  // Get category color
  const getCategoryColor = (categoryName) => {
    const colors = {
      'Entrées': '#10b981',
      'Plats Principaux': '#3b82f6',
      'Desserts': '#f59e0b',
      'Boissons': '#8b5cf6',
      'Apéritifs': '#ef4444',
      'Accompagnements': '#14b8a6'
    };
    return colors[categoryName] || '#6b7280';
  };

  return (
    <StyledCard elevation={0}>
      {/* Header with gradient */}
      <Box sx={{ 
        p: 2.5,
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar 
            sx={{ 
              width: 60, 
              height: 60, 
              bgcolor: '#e6f0ff',
              color: '#2563eb',
              borderRadius: '12px',
              border: '2px solid #ffffff',
              boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
            }}
          >
            <Restaurant sx={{ fontSize: 30 }} />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                {product.name}
              </Typography>
              {product.category && (
                <Chip 
                  label={product.category}
                  size="small"
                  sx={{ 
                    bgcolor: alpha(getCategoryColor(product.category), 0.1),
                    color: getCategoryColor(product.category),
                    fontSize: '0.7rem',
                    height: 22
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {product.sku || `REF-${String(index + 1).padStart(4, '0')}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Prix et coûts */}
        <SectionTitle>
          <PriceChange />
          TARIFICATION
        </SectionTitle>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <MetricBox sx={{ background: '#f0f9ff', borderColor: '#b8d1e0' }}>
              <Typography variant="caption" color="#0369a1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <AttachMoney sx={{ fontSize: 14 }} />
                Prix d'achat
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#0369a1">
                {(product.costPrice || 0).toFixed(3)} TND
              </Typography>
            </MetricBox>
          </Grid>
          <Grid item xs={6}>
            <MetricBox sx={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <Typography variant="caption" color="#166534" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TrendingUp sx={{ fontSize: 14 }} />
                Prix de vente
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#166534">
                {(product.soldPrice || 0).toFixed(3)} TND
              </Typography>
            </MetricBox>
          </Grid>
        </Grid>

        {/* Marge bénéficiaire */}
        <Box sx={{ 
          background: marginPercent > 30 ? '#f0fdf4' : marginPercent > 15 ? '#fff7ed' : '#fef2f2',
          borderRadius: '12px',
          p: 2,
          mb: 3,
          border: `1px solid ${marginPercent > 30 ? '#bbf7d0' : marginPercent > 15 ? '#fed7aa' : '#fecaca'}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600} color="#475569" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Balance sx={{ fontSize: 16 }} />
              Marge bénéficiaire
            </Typography>
            <Chip 
              label={`${marginPercent.toFixed(1)}%`}
              size="small"
              color={marginPercent > 30 ? 'success' : marginPercent > 15 ? 'warning' : 'error'}
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          </Box>
          <Typography variant="h5" fontWeight={700} color="#0f172a">
            {margin.toFixed(3)} TND
          </Typography>
          <Typography variant="caption" color="#64748b">
            Bénéfice par unité
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(marginPercent, 100)} 
            sx={{ 
              mt: 1.5, 
              height: 6, 
              borderRadius: 3,
              bgcolor: alpha(marginPercent > 30 ? '#4CAF50' : marginPercent > 15 ? '#FF9800' : '#F44336', 0.2),
              '& .MuiLinearProgress-bar': {
                bgcolor: marginPercent > 30 ? '#4CAF50' : marginPercent > 15 ? '#FF9800' : '#F44336'
              }
            }}
          />
        </Box>

        {/* Détails supplémentaires */}
        <SectionTitle>
          <Description />
          INFORMATIONS COMPLÉMENTAIRES
        </SectionTitle>

        <TableContainer component={Box} sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                <TableCell sx={{ borderColor: '#e2e8f0', color: '#64748b', pl: 0 }}>Unité de mesure</TableCell>
                <TableCell sx={{ borderColor: '#e2e8f0', fontWeight: 600 }} align="right">
                  {product.unit || 'Pièce'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ borderColor: '#e2e8f0', color: '#64748b', pl: 0 }}>Date d'ajout</TableCell>
                <TableCell sx={{ borderColor: '#e2e8f0', fontWeight: 600 }} align="right">
                  {new Date(product.createdAt || Date.now()).toLocaleDateString('fr-FR')}
                </TableCell>
              </TableRow>
              {product.ingredients && product.ingredients.length > 0 && (
                <TableRow>
                  <TableCell sx={{ borderColor: '#e2e8f0', color: '#64748b', pl: 0 }}>Ingrédients</TableCell>
                  <TableCell sx={{ borderColor: '#e2e8f0', fontWeight: 600 }} align="right">
                    {product.ingredients.length}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Notes */}
        {product.notes && (
          <Box sx={{ 
            p: 2, 
            bgcolor: '#fff7ed', 
            borderRadius: '12px',
            border: '1px solid #fed7aa',
            mb: 2
          }}>
            <Typography variant="caption" color="#9a3412" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Description sx={{ fontSize: 14 }} />
              Notes
            </Typography>
            <Typography variant="body2" color="#7c2d12">
              {product.notes}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          borderTop: '1px solid #e2e8f0',
          pt: 2,
          mt: 2
        }}>
          <Tooltip title="Modifier la fiche technique">
            <IconButton 
              onClick={() => handleEdit(product)}
              size="small"
              sx={{ 
                bgcolor: '#f1f5f9',
                color: '#475569',
                '&:hover': { bgcolor: '#2563eb', color: 'white' }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton 
              onClick={handleDelete}
              size="small"
              sx={{ 
                bgcolor: '#f1f5f9',
                color: '#ef4444',
                '&:hover': { bgcolor: '#ef4444', color: 'white' }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </StyledCard>
  );
};

// Main ProductList Component
export const ProductList = ({ products, handleEdit, handleDelete, onAddNew, onProductSelect }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [deleteDialog, setDeleteDialog] = useState({ open: false, productId: null, productName: '' });
  const [categories, setCategories] = useState([]);
  const restaurantId = localStorage.getItem("restaurantId");

  // Fetch categories when component mounts or products change
  useEffect(() => {
    fetchCategories();
  }, [products, restaurantId]);

  const fetchCategories = async () => {
    try {
      if (restaurantId) {
        const fetchedCategories = await Product.getCategories(restaurantId);
        setCategories(fetchedCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleDeleteClick = (productId, productName) => {
    setDeleteDialog({ open: true, productId, productName });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.productId) {
      handleDelete(deleteDialog.productId);
    }
    setDeleteDialog({ open: false, productId: null, productName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, productId: null, productName: '' });
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Get unique categories from products (excluding null/empty)
  const productCategories = [...new Set(products
    .map(p => p.category)
    .filter(cat => cat && cat.trim() !== '')
  )].sort();

  return (
    <Card sx={{ boxShadow: 'none', border: 'none', background: 'transparent' }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          p: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              color: '#0f172a',
              mb: 1
            }}>
              <Assignment sx={{ fontSize: 32, color: '#2563eb' }} />
              Fiches Techniques Produits
            </Typography>
            <Typography variant="body2" color="#475569">
              {products.length} produit{products.length > 1 ? 's' : ''} enregistré{products.length > 1 ? 's' : ''} | 
              {categories.length} catégorie{categories.length > 1 ? 's' : ''} | 
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ bgcolor: 'white' }}
            >
              <ToggleButton value="list">
                <ViewList fontSize="small" />
              </ToggleButton>
              <ToggleButton value="cards">
                <ViewModule fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            
            {onAddNew && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={onAddNew}
                sx={{
                  bgcolor: '#2563eb',
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                Nouveau Produit
              </Button>
            )}
          </Box>
        </Box>

        {/* Category Summary Chips */}
        {categories.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Category sx={{ fontSize: 14 }} />
              Catégories:
            </Typography>
            {categories.map(cat => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                variant="outlined"
                sx={{ borderRadius: '12px' }}
              />
            ))}
          </Box>
        )}

        {/* Conditional Rendering of Views */}
        {viewMode === 'list' ? (
          <ProductListView 
            products={products}
            handleEdit={handleEdit}
            handleDelete={handleDeleteClick}
            onProductSelect={onProductSelect}
            categories={productCategories}
          />
        ) : (
          <ProductCardView 
            products={products}
            handleEdit={handleEdit}
            handleDelete={handleDeleteClick}
            categories={productCategories}
          />
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxWidth: 450,
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          p: 3,
          textAlign: 'center'
        }}>
          <Avatar sx={{
            width: 70,
            height: 70,
            bgcolor: 'rgba(255,255,255,0.2)',
            margin: '0 auto 12px',
            border: '2px solid white'
          }}>
            <WarningIcon sx={{ fontSize: 40, color: 'white' }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
            Confirmer la suppression
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Cette action est irréversible
          </Typography>
        </Box>

        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#1f2937' }}>
            Êtes-vous sûr de vouloir supprimer définitivement
          </Typography>
          <Chip
            label={deleteDialog.productName}
            sx={{
              bgcolor: '#fee2e2',
              color: '#dc2626',
              fontWeight: 600,
              fontSize: '1rem',
              height: 36,
              px: 2,
              border: '1px solid #fecaca',
              mb: 3
            }}
          />
          <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
            Cette action supprimera toutes les données associées à ce produit
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            startIcon={<Close />}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteForever />}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                transform: 'scale(1.02)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ProductList;